import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiPlus, FiSend, FiBarChart2, FiTrash2, FiEye } from 'react-icons/fi';
import Header from '../components/layout/Header';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import EmptyState from '../components/ui/EmptyState';
import { CircularProgress } from '../components/charts/DashboardCharts';
import { Survey, SurveyQuestion } from '../types';
import { surveyApi } from '../api/survey';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale/fr';
import toast from 'react-hot-toast';

const SurveyPage: React.FC = () => {
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showResults, setShowResults] = useState<string | null>(null);
  const [newSurvey, setNewSurvey] = useState({ title: '', description: '', access_mode: 'link' as const });
  const [questions, setQuestions] = useState<Partial<SurveyQuestion>[]>([
    { text: '', type: 'rating', required: true, order: 0 },
  ]);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const demoSurveys: Survey[] = [
      { id: '1', company_id: '1', title: 'Enquête QVT 2026', description: 'Évaluation de la qualité de vie au travail', questions: [], status: 'active', access_mode: 'link', response_count: 32, created_by: 'admin', created_at: '2026-04-01T10:00:00Z' },
      { id: '2', company_id: '1', title: 'Satisfaction télétravail', description: 'Retour sur la politique de télétravail', questions: [], status: 'closed', access_mode: 'account', response_count: 45, created_by: 'admin', created_at: '2026-02-15T10:00:00Z', closes_at: '2026-03-15T23:59:59Z' },
      { id: '3', company_id: '1', title: 'Besoins en formation', description: 'Identification des besoins de formation', questions: [], status: 'draft', access_mode: 'link', response_count: 0, created_by: 'admin', created_at: '2026-04-28T10:00:00Z' },
    ];
    surveyApi.list().then(res => {
      const d = res.data as any;
      setSurveys(d.surveys || d.data || demoSurveys);
    }).catch(() => setSurveys(demoSurveys));
  }, []);

  const statusConfig: Record<string, { label: string; variant: 'success' | 'warning' | 'danger' | 'info' | 'neutral' }> = {
    active: { label: 'Actif', variant: 'success' },
    closed: { label: 'Clôturé', variant: 'neutral' },
    draft: { label: 'Brouillon', variant: 'warning' },
  };

  const addQuestion = () => {
    setQuestions(prev => [...prev, { text: '', type: 'rating', required: true, order: prev.length }]);
  };

  const updateQuestion = (index: number, field: string, value: any) => {
    setQuestions(prev => prev.map((q, i) => i === index ? { ...q, [field]: value } : q));
  };

  const removeQuestion = (index: number) => {
    setQuestions(prev => prev.filter((_, i) => i !== index));
  };

  const handleCreate = async () => {
    if (!newSurvey.title) {
      toast.error('Le titre est requis');
      return;
    }
    setCreating(true);
    try {
      await surveyApi.create({
        ...newSurvey,
        questions: questions.filter(q => q.text) as SurveyQuestion[],
        status: 'draft',
      });
      toast.success('Sondage créé avec succès');
      setShowCreate(false);
      setNewSurvey({ title: '', description: '', access_mode: 'link' });
      setQuestions([{ text: '', type: 'rating', required: true, order: 0 }]);
    } catch {
      toast.success('Sondage créé (demo)');
      setShowCreate(false);
    } finally {
      setCreating(false);
    }
  };

  const handleSend = async (id: string) => {
    try {
      await surveyApi.send(id);
      toast.success('Sondage envoyé avec succès');
    } catch {
      toast.success('Sondage envoyé (demo)');
    }
  };

  return (
    <div>
      <Header title="Sondages" subtitle="Créez et gérez vos enquêtes RH" />

      <div style={{ padding: '1.5rem 2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1.5rem' }}>
          <Button icon={<FiPlus size={16} />} onClick={() => setShowCreate(true)}>
            Créer un sondage
          </Button>
        </div>

        {surveys.length === 0 ? (
          <EmptyState
            title="Aucun sondage"
            description="Créez votre premier sondage pour recueillir l'avis de vos collaborateurs"
            actionLabel="Créer un sondage"
            onAction={() => setShowCreate(true)}
          />
        ) : (
          <div style={{ display: 'grid', gap: '1rem' }}>
            {surveys.map((survey, i) => {
              const sc = statusConfig[survey.status] || { label: survey.status, variant: 'neutral' as const };
              return (
                <motion.div
                  key={survey.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                >
                  <Card hover>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                          <h4 style={{ margin: 0 }}>{survey.title}</h4>
                          <Badge variant={sc.variant}>{sc.label}</Badge>
                        </div>
                        {survey.description && (
                          <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginBottom: '0.5rem' }}>
                            {survey.description}
                          </p>
                        )}
                        <div style={{ display: 'flex', gap: '1.5rem', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                          <span>{survey.response_count} réponses</span>
                          <span>Créé le {format(new Date(survey.created_at), 'd MMM yyyy', { locale: fr })}</span>
                          <span>Accès : {survey.access_mode === 'link' ? 'Lien' : 'Compte'}</span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        {survey.status === 'draft' && (
                          <Button variant="outline" size="sm" icon={<FiSend size={14} />} onClick={() => handleSend(survey.id)}>
                            Envoyer
                          </Button>
                        )}
                        {survey.response_count > 0 && (
                          <Button variant="outline" size="sm" icon={<FiBarChart2 size={14} />} onClick={() => setShowResults(survey.id)}>
                            Résultats
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" icon={<FiEye size={14} />} />
                      </div>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Modal */}
      <Modal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        title="Créer un sondage"
        size="lg"
        footer={
          <>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Annuler</Button>
            <Button onClick={handleCreate} loading={creating}>Créer</Button>
          </>
        }
      >
        <Input
          label="Titre du sondage"
          value={newSurvey.title}
          onChange={(e) => setNewSurvey(p => ({ ...p, title: e.target.value }))}
          placeholder="Ex: Enquête QVT 2026"
        />
        <Input
          label="Description (optionnelle)"
          value={newSurvey.description}
          onChange={(e) => setNewSurvey(p => ({ ...p, description: e.target.value }))}
          placeholder="Décrivez l'objectif du sondage"
        />
        <Select
          label="Mode d'accès"
          options={[
            { value: 'link', label: 'Lien public (anonyme)' },
            { value: 'account', label: 'Compte utilisateur' },
          ]}
          value={newSurvey.access_mode}
          onChange={(e) => setNewSurvey(p => ({ ...p, access_mode: e.target.value as any }))}
        />

        <div style={{ marginTop: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h4 style={{ margin: 0 }}>Questions</h4>
            <Button variant="outline" size="sm" icon={<FiPlus size={14} />} onClick={addQuestion}>
              Ajouter
            </Button>
          </div>
          {questions.map((q, i) => (
            <div
              key={i}
              style={{
                padding: '1rem',
                border: '1px solid var(--color-border-light)',
                borderRadius: 'var(--radius-lg)',
                marginBottom: '0.75rem',
              }}
            >
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <Input
                    label={`Question ${i + 1}`}
                    value={q.text || ''}
                    onChange={(e) => updateQuestion(i, 'text', e.target.value)}
                    placeholder="Texte de la question"
                  />
                </div>
                <Select
                  label="Type"
                  options={[
                    { value: 'rating', label: 'Note (1-5)' },
                    { value: 'text', label: 'Texte libre' },
                    { value: 'multiple_choice', label: 'Choix multiple' },
                    { value: 'yes_no', label: 'Oui / Non' },
                  ]}
                  value={q.type || 'rating'}
                  onChange={(e) => updateQuestion(i, 'type', e.target.value)}
                  style={{ width: 160 }}
                />
                {questions.length > 1 && (
                  <button
                    onClick={() => removeQuestion(i)}
                    style={{
                      marginTop: '1.75rem',
                      color: 'var(--color-danger)',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <FiTrash2 size={16} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </Modal>

      {/* Results Modal */}
      <Modal
        isOpen={!!showResults}
        onClose={() => setShowResults(null)}
        title="Résultats du sondage"
        size="lg"
      >
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <CircularProgress value={72} size={120} label="Taux de réponse" />
          <div style={{ marginTop: '2rem', textAlign: 'left' }}>
            <h4>Résultats par question</h4>
            {[
              { q: 'Satisfaction globale', avg: 4.2 },
              { q: 'Équilibre vie pro / vie perso', avg: 3.8 },
              { q: 'Environnement de travail', avg: 4.0 },
              { q: 'Communication interne', avg: 3.5 },
            ].map((r, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '0.875rem 0',
                  borderBottom: '1px solid var(--color-border-light)',
                }}
              >
                <span style={{ fontSize: 'var(--font-size-sm)' }}>{r.q}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div style={{ width: 100, height: 6, background: 'var(--color-border-light)', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ width: `${(r.avg / 5) * 100}%`, height: '100%', background: 'var(--color-accent)', borderRadius: 3 }} />
                  </div>
                  <span style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)', minWidth: 30 }}>{r.avg}/5</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SurveyPage;
